// Digadop 2.0 hub embed bridge.
//
// The Janus hub (app.digadop.com in 2.0) embeds this admin app in an iframe as
// the "Help center" area while the native hub rewrite ports areas in one at a
// time (the strangler bridge). Two things differ from the standalone app and
// from the existing Salesforce-LWC embed:
//
//   1. Chrome: the hub already renders its own shell (top bar, product nav,
//      account menu), so the admin must hide ITS nav/footer/banner/floating
//      help and render only the routed content.
//   2. Session: the hub owns auth. It cannot set this app's cookies across
//      origins, so it hands us the session over postMessage instead of the
//      Salesforce popup / org-wide-token flow.
//
// This module is deliberately host-mechanism-agnostic: it works whether the hub
// serves us from an iframe pointed at the standalone build or a same-origin
// mount. The one hard requirement is a trusted postMessage channel, gated by an
// origin allowlist below.

const EMBED_FLAG_KEY = "digadop_hub_embedded";
const CHROME_MODE_KEY = "digadop_hub_chrome";
const MSG_NS = "digadop-hub";

// Message types (both directions share the namespace prefix).
export const HUB_MSG = {
  // hub -> app
  SESSION: MSG_NS + ":session", // { user: <react-client user object> }
  NAVIGATE_TO: MSG_NS + ":navigate-to", // { path: "/admin/content" } deep-link from hub
  // app -> hub
  READY: MSG_NS + ":ready", // app mounted, send me the session
  NAVIGATED: MSG_NS + ":navigated", // { path } route changed inside the app
  HEIGHT: MSG_NS + ":height", // { height } content height for iframe auto-size
  SESSION_ACK: MSG_NS + ":session-ack", // { orgId, userId } session accepted
  SESSION_EXPIRED: MSG_NS + ":session-expired", // token rejected/expired, re-mint please
  LOGOUT: MSG_NS + ":logout", // BIDIRECTIONAL. app -> hub: user asked to sign
  // out and the hub owns the session. hub -> app: the hub signed out, so the
  // embed must drop its session too (#411 Phase 3.3).
};

// How many times, and how often, to re-announce READY if the hub has not sent a
// session yet. Guards the race where the app mounts before the hub installs its
// message listener. Bounded so it stops once a session lands or the budget runs
// out (the hub may simply not be there, e.g. opened standalone with the flag).
const READY_REANNOUNCE_MS = 400;
const READY_REANNOUNCE_MAX = 8;

// The trusted-origin allowlist: which parent frames may send us hub->app control
// messages (session, navigate-to, logout). This is THE security boundary for the
// control channel, and it is the single choke point for it.
//
// #434. This used to be a pattern: any `*.digadop.com` over https, plus any
// localhost port, IDENTICALLY IN EVERY BUILD. Two problems with that:
//
//   1. A prod bundle trusted every present and future digadop.com subdomain,
//      including qa hosts and test pages, plus `http://localhost:<any port>`.
//      Prod has exactly one legitimate parent, so everything else was surface we
//      got nothing for.
//   2. Because prod's list was identical to qa's, adding a qa test origin
//      (e.g. the #433 embed harness) added an origin PROD also trusted. That is
//      what made "is this test origin safe?" a question at all.
//
// So the list is now build-time configuration, matching the REACT_APP_FEATURES
// pattern in Helpers/features.js. Build-time rather than runtime is deliberate:
// what a build trusts is a property of that build hash and can be read back out
// of the bundle after the fact, not guessed from a config service.
//
//   build:prod -> ""                              (no prod hub EXISTS yet)
//   build:qa   -> "https://hub-qa.digadop.com ..."
//   start:dev  -> "https://localhost:3000 ..."
//
// THE SAFETY PROPERTY: an unset or empty list trusts NOTHING. A build that
// nobody configured cannot be driven by any parent, so the failure mode of
// forgetting is a visibly dead embed rather than a silently permissive one.
// Prod's list is empty on purpose: `hub.digadop.com` does not resolve, so there
// is no prod hub to trust, and prod should therefore trust no parent at all.
//
// Matching is on the full serialized ORIGIN (scheme + host + port), exactly.
// No subdomain wildcards, no protocol coercion: `http://x` and `https://x` are
// different origins, which is the same rule the browser applies.
function trustedHubOrigins() {
  const raw = process.env.REACT_APP_TRUSTED_HUB_ORIGINS;
  if (typeof raw !== "string" || !raw.trim()) return [];
  const out = [];
  raw
    .split(/[,\s]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .forEach((entry) => {
      // Normalise each configured entry through the URL parser too, so a
      // trailing slash or an uppercase host in the build script cannot cause a
      // silent mismatch against a correctly-formed incoming origin.
      try {
        const o = new URL(entry).origin;
        if (o && o !== "null" && out.indexOf(o) === -1) out.push(o);
      } catch (err) {
        // A malformed entry is a build-script typo. Skip it rather than
        // trusting it; the warning below surfaces it in non-prod.
      }
    });
  return out;
}

/** The configured trusted hub origins for this build. Exported for diagnostics. */
export function configuredHubOrigins() {
  return trustedHubOrigins();
}

export function isTrustedHubOrigin(origin) {
  if (typeof origin !== "string" || !origin) {
    return false;
  }
  const allowed = trustedHubOrigins();
  if (!allowed.length) {
    return false;
  }
  let incoming;
  try {
    // The postMessage `null` origin (sandboxed / file / opaque frames) parses as
    // the string "null" and must never match.
    incoming = new URL(origin).origin;
  } catch (err) {
    return false;
  }
  if (!incoming || incoming === "null") {
    return false;
  }
  const ok = allowed.indexOf(incoming) !== -1;
  // Gated to development specifically, not "not production": the test suite
  // asserts dozens of deliberate rejections and should not print a warning for
  // each one.
  if (!ok && process.env.NODE_ENV === "development") {
    // Exact matching means a dev on an unexpected port gets a dead bridge with
    // no clue why. Say why, in non-prod only, so the fix is obvious without
    // loosening the boundary to make dev convenient.
    // eslint-disable-next-line no-console
    console.warn(
      "[hubEmbed] dropped a hub message from untrusted origin " +
        incoming +
        "; REACT_APP_TRUSTED_HUB_ORIGINS allows [" +
        allowed.join(", ") +
        "]"
    );
  }
  return ok;
}

// True when this load is (or has been, this browsing session) inside the hub.
// The ?embedded=hub query param is authoritative on first load; we latch it to
// sessionStorage so client-side navigation that drops the query string keeps the
// embedded chrome. window.self !== window.top alone is NOT used: the Salesforce
// LWC help panel is also framed and must keep its own presentation.
export function isHubEmbedded() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("embedded") === "hub") {
      window.sessionStorage.setItem(EMBED_FLAG_KEY, "1");
      return true;
    }
    // #411, found by qa Case 8: the latch could go STALE and leak embedded
    // chrome into a genuine standalone load. The latch exists so client-side
    // navigation inside the frame, which drops the query string, keeps embedded
    // chrome. But a top-level document with no flag is definitively NOT the hub
    // embed: the hub always mounts us in an iframe, so window.self !== window.top
    // there. So an unframed load with no flag clears the latch instead of
    // honouring it.
    //
    // Deliberately only CLEARS on this condition, never sets. A framed load with
    // no flag still honours the latch, which is the in-frame navigation case, and
    // the Salesforce LWC panel is unaffected because it never sets the latch.
    if (window.self === window.top) {
      window.sessionStorage.removeItem(EMBED_FLAG_KEY);
      return false;
    }
    return window.sessionStorage.getItem(EMBED_FLAG_KEY) === "1";
  } catch (err) {
    // sessionStorage can throw in locked-down iframes; fall back to the live param.
    try {
      return new URLSearchParams(window.location.search).get("embedded") === "hub";
    } catch (err2) {
      return false;
    }
  }
}

// Chrome granularity for the embed. Default 'full' keeps a minimal in-frame area
// nav (Content/Layouts/...) so the embed is self-sufficient; ?chrome=none hides
// it so the hub can own all navigation. Latched like the embed flag.
export function hubChromeMode() {
  try {
    const params = new URLSearchParams(window.location.search);
    const q = params.get("chrome");
    if (q === "none" || q === "full") {
      window.sessionStorage.setItem(CHROME_MODE_KEY, q);
      return q;
    }
    return window.sessionStorage.getItem(CHROME_MODE_KEY) || "full";
  } catch (err) {
    return "full";
  }
}

// True when the embed should render its own minimal area nav (i.e. embedded and
// the hub has not asked to own the chrome).
export function showEmbedNav() {
  return isHubEmbedded() && hubChromeMode() !== "none";
}

// Post a message up to the hub. No-op when not embedded or there is no parent.
export function postToHub(type, payload) {
  if (!isHubEmbedded()) {
    return;
  }
  try {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(
        { type, source: MSG_NS, payload: payload || {} },
        "*" // read-only outbound (nav/height/ready); no secrets leave the app
      );
    }
  } catch (err) {
    /* ignore cross-origin post failures */
  }
}

// Tell the hub the current session was rejected/expired and a fresh one is
// needed. The hub re-mints and posts a new SESSION. No-op when not embedded.
export function notifyHubSessionExpired() {
  postToHub(HUB_MSG.SESSION_EXPIRED, {});
}

// Tell the hub the user asked to sign out. Inside the hub the app must NOT clear
// its own session and drop to the standalone Salesforce login; the hub owns the
// session and drives logout across all its areas. Returns true when it handled
// the logout (embedded), so the caller can skip its own local sign-out.
export function requestHubLogout() {
  if (!isHubEmbedded()) {
    return false;
  }
  postToHub(HUB_MSG.LOGOUT, {});
  return true;
}

// Wire the hub->app channel. Calls handlers.onSession(user) when the hub sends a
// trusted session, and handlers.onNavigateTo(path) for a hub deep-link. Returns
// a cleanup function. Safe to call when not embedded (it just posts nothing and
// installs a listener that ignores everything untrusted). Re-announces READY a
// bounded number of times until a session arrives, to survive the mount-order
// race with the hub's own listener.
export function initHubBridge(handlers) {
  let gotSession = false;
  const onMessage = (event) => {
    const data = event && event.data;
    if (!data || data.source !== MSG_NS || typeof data.type !== "string") {
      return;
    }
    // Control messages that carry authority (session, navigation) MUST come from
    // a trusted hub origin. This is the security boundary for the bridge.
    if (!isTrustedHubOrigin(event.origin)) {
      return;
    }
    if (data.type === HUB_MSG.SESSION && data.payload && data.payload.user) {
      gotSession = true;
      if (handlers && handlers.onSession) {
        handlers.onSession(data.payload.user);
      }
    } else if (data.type === HUB_MSG.NAVIGATE_TO && data.payload && data.payload.path) {
      if (handlers && handlers.onNavigateTo) {
        handlers.onNavigateTo(data.payload.path);
      }
    } else if (data.type === HUB_MSG.LOGOUT) {
      // #411 Phase 3.3: the hub signed the user out. LOGOUT was previously
      // app -> hub only, so signing out of the hub left an AUTHENTICATED admin
      // session alive inside the frame, reachable on a browser Back or a
      // restored tab. The session must die with the hub session.
      gotSession = false;
      if (handlers && handlers.onHubLogout) {
        handlers.onHubLogout();
      }
    }
  };
  window.addEventListener("message", onMessage);
  // Tell the hub we are mounted and ready to receive the session, then
  // re-announce a few times in case the hub attached its listener after us.
  postToHub(HUB_MSG.READY, {});
  let tries = 0;
  const readyTimer = setInterval(() => {
    tries += 1;
    if (gotSession || tries >= READY_REANNOUNCE_MAX) {
      clearInterval(readyTimer);
      return;
    }
    postToHub(HUB_MSG.READY, {});
  }, READY_REANNOUNCE_MS);
  return () => {
    window.removeEventListener("message", onMessage);
    clearInterval(readyTimer);
  };
}

// #411 Phase 3.1: validate a hub-supplied navigation target.
//
// `navigate-to` carries a path over postMessage. The origin check already
// establishes that a TRUSTED hub sent it, but trusted is not the same as
// correct: a hub bug, or a hub compromise, must not be able to navigate this
// frame off our own origin. So the path is validated as a same-app relative
// route, and anything else is refused rather than coerced into something
// plausible.
//
// Returns the safe path, or null if it must be refused.
export function safeEmbedPath(path) {
  if (typeof path !== "string") return null;
  const p = path.trim();
  if (!p) return null;
  // Must be an app-relative route.
  if (p.charAt(0) !== "/") return null;
  // "//host" is protocol-relative and resolves to a DIFFERENT origin.
  if (p.charAt(1) === "/") return null;
  // Browsers normalise backslashes to slashes, so "/\\evil.com" becomes
  // "//evil.com". Refuse them outright rather than trying to normalise.
  if (p.indexOf("\\") !== -1) return null;
  // A scheme before the first slash would mean this is not relative at all.
  const firstSlash = p.indexOf("/", 1);
  const head = firstSlash === -1 ? p.slice(1) : p.slice(1, firstSlash);
  if (head.indexOf(":") !== -1) return null;
  return p;
}
